import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Icon from '../components/Icon';

const IMG = `${process.env.PUBLIC_URL}/images`;
const FIMG = `${IMG}/Founder`;

const traits = [
  {
    icon: 'users',
    title: 'People First',
    desc: 'Every decision begins with people.',
  },
  {
    icon: 'lightbulb',
    title: 'Think Big',
    desc: 'Dream audaciously, build relentlessly.',
  },
  {
    icon: 'hand-heart',
    title: 'Create Impact',
    desc: 'Building solutions that create lasting impact.',
  },
  {
    icon: 'leaf',
    title: 'Stay Rooted',
    desc: 'Values, culture and purpose keep us grounded.',
  },
];

const journey = [
  {
    icon: 'rocket',
    title: 'Early Curiosity',
    desc: 'Fascinated by technology and its ability to transform lives.',
  },
  {
    icon: 'flag',
    title: 'Overcoming Challenges',
    desc: 'Every challenge became a lesson that shaped my vision.',
  },
  {
    icon: 'lightbulb',
    title: 'The Big Insight',
    desc: 'Realized the event industry was fragmented and needed innovation.',
  },
  {
    icon: 'leaf',
    title: 'Caladium Systems Is Born',
    desc: 'A vision to build ecosystems that connect emotion, technology and commerce.',
  },
  {
    icon: 'globe',
    title: 'Building for the Future',
    desc: 'Continuing the journey to impact millions of celebrations worldwide.',
  },
];

const drives = [
  'Solving meaningful problems',
  'Building and empowering teams',
  'Creating value for customers and partners',
  'Innovation that leaves a legacy',
];

const principles = [
  {
    icon: 'flag',
    title: 'Lead with Purpose',
    desc: 'A clear purpose inspires teams and creates extraordinary outcomes.',
  },
  {
    icon: 'users',
    title: 'Empower People',
    desc: 'I believe in empowering teams to think, own and create.',
  },
  {
    icon: 'lightbulb',
    title: 'Embrace Innovation',
    desc: 'Constantly question, constantly innovate, constantly evolve.',
  },
  {
    icon: 'handshake',
    title: 'Build with Integrity',
    desc: 'Trust, transparency and honesty are non-negotiable in everything we do.',
  },
  {
    icon: 'globe',
    title: 'Think Global, Impact Local',
    desc: 'Build globally, but always create value for local communities.',
  },
];

const media = [
  {
    title: 'The Future of Celebrations Summit 2024',
    videoId: 'qrf1ftzrgVk',
  },
  {
    title: 'Interview with ET Now Leadership Series',
    videoId: '6ltoF8zA1xc',
  },
  {
    title: 'Building Ecosystems, Not Just Platforms',
    videoId: 'nw3DaiTO0Ao',
  },
  {
    title: 'Podcast: The Makers of Tomorrow',
    videoId: 'gkzdNo4M-Hc',
  },
].map((m) => ({
  ...m,
  img: `https://img.youtube.com/vi/${m.videoId}/hqdefault.jpg`,
  url: `https://www.youtube.com/watch?v=${m.videoId}`,
}));

export default function FounderPage() {
  const [activeVideo, setActiveVideo] = useState(null);

  // Lock body scroll + close on Escape while the popup is open.
  useEffect(() => {
    if (!activeVideo) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') setActiveVideo(null);
    };
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', onKey);
    };
  }, [activeVideo]);

  return (
    <div className="fnd-page">
      {/* =================== HERO =================== */}
      <section className="fnd-hero">
        <div
          className="fnd-hero__bg"
          style={{ backgroundImage: `url(${FIMG}/Founder-1.jpeg)` }}
        />
        <div className="container container--wide fnd-hero__inner">
          <div className="crumb">
            <Link to="/">Home</Link>
            <span>/</span>
            <span>Founder</span>
          </div>

          <div className="fnd-hero__row">
            <div className="fnd-hero__copy">
              <span className="eyebrow">FOUNDER &amp; VISIONARY</span>
              <h1 className="fnd-hero__title">
                Building the Future
                <br />
                of <span className="grad-pink">Human Experiences</span>
                <br />
                and <span className="grad-pink">Celebrations.</span>
              </h1>
              <p className="fnd-hero__lead">
                Caladium Systems is more than a company. It&rsquo;s a vision to
                transform the way the world celebrates, connects and creates
                meaningful memories.
              </p>
              <button type="button" className="play-cta">
                <span className="play-cta__btn">
                  <Icon name="play" size={20} />
                </span>
                <span className="play-cta__text">
                  <strong>Watch Founder&rsquo;s Message</strong>
                  <span>2:35 min</span>
                </span>
              </button>
            </div>

            <div className="fnd-hero__visual">
              <img
                className="fnd-portrait__photo"
                src={`${IMG}/Caladium_MD.png`}
                alt="Pradhyumna T Venkat"
              />
              <div className="fnd-signature">
                <span className="fnd-signature__name">Pradhyumna T Venkat</span>
                <span className="fnd-signature__role">Founder &amp; Visionary</span>
                <span className="fnd-signature__role">Caladium Systems</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* =================== QUOTE + TRAITS =================== */}
      <section className="fnd-quote-sec">
        <div className="container container--wide">
          <div className="fnd-quote">
            <div className="fnd-quote__text">
              <span className="fnd-quote__mark">&ldquo;</span>
              <p>
                My dream is to build intelligent ecosystems that bring joy to
                millions and create opportunities for countless people around
                the world.
              </p>
            </div>
            <div className="fnd-traits">
              {traits.map((t) => (
                <div className="fnd-trait" key={t.title}>
                  <span className="fnd-trait__icon">
                    <Icon name={t.icon} size={26} />
                  </span>
                  <strong>{t.title}</strong>
                  <span>{t.desc}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* =================== FOUNDER STORY =================== */}
      <section className="fnd-story-sec">
        <div className="container container--wide fnd-story">
          <div className="fnd-story__copy">
            <span className="eyebrow">THE FOUNDER&rsquo;S STORY</span>
            <h2 className="h2">
              From Curiosity to Creating Intelligent Ecosystems
            </h2>
            <p>
              My journey has been driven by curiosity, challenges and a deep
              desire to solve real problems. From my early days of exploring
              technology to building platforms that power celebrations today,
              it has been an incredible journey of learning, unlearning and
              growing.
            </p>
            <button type="button" className="btn btn--outline">
              Read My Full Story <Icon name="arrow-right" size={16} />
            </button>
          </div>

          <div className="fnd-timeline">
            <div className="fnd-timeline__track">
              {journey.map((j) => (
                <div className="fnd-mile" key={j.title}>
                  <span className="fnd-mile__dot">
                    <Icon name={j.icon} size={22} />
                  </span>
                  <h3>{j.title}</h3>
                  <p>{j.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* =================== VISION / PHILOSOPHY / DRIVES =================== */}
      <section className="fnd-pillars-sec">
        <div className="container container--wide fnd-pillars">
          <div className="fnd-pillar fnd-pillar--vision">
            <span className="eyebrow">MY VISION</span>
            <div className="fnd-pillar__body">
              <div
                className="fnd-pillar__visual"
                style={{ backgroundImage: `url(${FIMG}/Founder-2.jpeg)` }}
              />
              <p>
                To become the global leader in experience ecosystems, where
                technology, creativity and empathy converge to elevate every
                human celebration.
              </p>
            </div>
          </div>

          <div className="fnd-pillar fnd-pillar--philosophy">
            <span className="eyebrow">MY PHILOSOPHY</span>
            <span className="fnd-pillar__qmark">
              <Icon name="quote" size={30} />
            </span>
            <p>
              Technology is only powerful when it amplifies human emotions.
              We build platforms, but we create experiences.
            </p>
          </div>

          <div className="fnd-pillar fnd-pillar--drives">
            <span className="eyebrow">WHAT DRIVES ME</span>
            <ul className="fnd-drives">
              {drives.map((d) => (
                <li key={d}>
                  <span className="fnd-drives__dot">
                    <Icon name="check" size={13} />
                  </span>
                  {d}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* =================== LEADERSHIP PRINCIPLES =================== */}
      <section className="fnd-leaders-sec">
        <div className="container container--wide">
          <header className="section__head">
            <h2 className="h2">My Leadership Principles</h2>
          </header>
          <div className="fnd-leaders">
            {principles.map((p) => (
              <div className="fnd-leader" key={p.title}>
                <span className="fnd-leader__icon">
                  <Icon name={p.icon} size={32} />
                </span>
                <h3>{p.title}</h3>
                <p>{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* =================== IN THE MEDIA =================== */}
      <section className="fnd-media-sec">
        <div className="container container--wide">
          <div className="fnd-media">
            <div className="fnd-media__copy">
              <h2 className="h2">In The Media</h2>
              <p>Moments from interviews, podcasts and talks.</p>
            </div>
            <div className="fnd-media__grid">
              {media.map((m) => (
                <button
                  type="button"
                  className="fnd-clip"
                  key={m.title}
                  onClick={() => setActiveVideo(m)}
                >
                  <div className="fnd-clip__media">
                    <img src={m.img} alt={m.title} />
                    <span className="fnd-clip__play">
                      <Icon name="play" size={18} />
                    </span>
                  </div>
                  <h4 className="fnd-clip__title">{m.title}</h4>
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* =================== VIDEO POPUP =================== */}
      {activeVideo && (
        <div
          className="video-modal"
          onClick={() => setActiveVideo(null)}
          role="dialog"
          aria-modal="true"
        >
          <div
            className="video-modal__inner"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              className="video-modal__close"
              onClick={() => setActiveVideo(null)}
              aria-label="Close video"
            >
              &times;
            </button>
            <div className="video-modal__frame">
              <iframe
                src={`https://www.youtube.com/embed/${activeVideo.videoId}?autoplay=1&rel=0`}
                title={activeVideo.title}
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
              />
            </div>
            <p className="video-modal__title">{activeVideo.title}</p>
          </div>
        </div>
      )}
    </div>
  );
}
